/**
 * Revue / validation d'une session transversale (Manager / Admin).
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  ApiRequestError,
  transversalAssessmentApi,
  type TransversalAssessmentDetailDto,
} from "../api/api";
import { PageHeader } from "../components/ui/PageHeader";
import { StatusBadge } from "../components/ui/StatusBadge";
import { useAuth } from "../context/AuthContext";

export function TransversalAssessmentReviewPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const { user } = useAuth();
  const level = user?.accessLevel ?? 0;

  const [assessment, setAssessment] = useState<TransversalAssessmentDetailDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [returnComment, setReturnComment] = useState("");
  const [showReturn, setShowReturn] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setError(null);
    try {
      const res = await transversalAssessmentApi.get(id);
      setAssessment(res.assessment);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t("load_failed"));
      setAssessment(null);
    }
  }, [id, t]);

  useEffect(() => {
    void load();
  }, [load]);

  async function onValidate() {
    if (!id) return;
    setBusy(true);
    try {
      const res = await transversalAssessmentApi.validate(id);
      setAssessment(res.assessment);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t("validate_failed"));
    } finally {
      setBusy(false);
    }
  }

  async function onReturn() {
    if (!id || !returnComment.trim()) return;
    setBusy(true);
    try {
      const res = await transversalAssessmentApi.return(id, returnComment.trim());
      setAssessment(res.assessment);
      setShowReturn(false);
      setReturnComment("");
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t("return_failed"));
    } finally {
      setBusy(false);
    }
  }

  if (!assessment && !error) {
    return (
      <div className="sm-page">
        <p className="sm-muted">{t("loading")}</p>
      </div>
    );
  }

  if (!assessment) {
    return (
      <div className="sm-page">
        <div className="sm-alert sm-alert-error">{error}</div>
        <Link to="/validation" className="sm-link">
          ← {t("validation_dashboard")}
        </Link>
      </div>
    );
  }

  const canValidate = level >= 50 && assessment.status === "submitted";
  const scored = assessment.scores.filter((s) => s.score !== null).length;

  return (
    <div className="sm-page">
      <PageHeader
        title={t("transversal_validation_title")}
        subtitle={`${assessment.userName} · ${assessment.year}`}
      />

      {error ? <div className="sm-alert sm-alert-error">{error}</div> : null}

      <div className="sm-card sm-card-pad" style={{ marginBottom: 16 }}>
        <StatusBadge status={assessment.status} />{" "}
        <span className="sm-muted">
          {t("lines_scored")}: {scored}/{assessment.lineCount}
        </span>
        {canValidate ? (
          <div className="sm-form-actions" style={{ marginTop: 12 }}>
            <button type="button" className="sm-btn sm-btn-primary" disabled={busy} onClick={() => void onValidate()}>
              {t("validate")}
            </button>
            <button type="button" className="sm-btn sm-btn-secondary" disabled={busy} onClick={() => setShowReturn(true)}>
              {t("return_to_expert")}
            </button>
          </div>
        ) : null}
      </div>

      {showReturn ? (
        <div className="sm-card sm-card-pad" style={{ marginBottom: 16 }}>
          <textarea
            className="sm-input"
            rows={3}
            value={returnComment}
            onChange={(e) => setReturnComment(e.target.value)}
            placeholder={t("return_comment_placeholder")}
          />
          <div className="sm-form-actions" style={{ marginTop: 8 }}>
            <button type="button" className="sm-btn sm-btn-primary" disabled={busy} onClick={() => void onReturn()}>
              {t("confirm_return")}
            </button>
            <button type="button" className="sm-btn sm-btn-secondary" onClick={() => setShowReturn(false)}>
              {t("cancel")}
            </button>
          </div>
        </div>
      ) : null}

      <div className="sm-table-wrap sm-card">
        <table className="sm-table">
          <thead>
            <tr>
              <th>{t("transversal_skills")}</th>
              <th style={{ width: 64 }}>{t("score")}</th>
              <th>{t("comment")}</th>
            </tr>
          </thead>
          <tbody>
            {assessment.scores.map((s) => (
              <tr key={s.transversalSkillId}>
                <td>
                  {s.macroProcess} / {s.businessProcess}
                  {s.subProcess ? ` / ${s.subProcess}` : ""}
                  {s.task ? ` — ${s.task}` : ""}
                </td>
                <td>{s.score ?? "—"}</td>
                <td className="sm-muted">{s.comment ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p style={{ marginTop: 16 }}>
        <Link to="/validation" className="sm-link">
          ← {t("validation_dashboard")}
        </Link>
      </p>
    </div>
  );
}
